<template>
  <nav v-if="!hideGuestTableNav" class="border-b border-cyan-400/10 bg-slate-950/95 backdrop-blur-xl sticky top-0 z-40">
    <div class="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
      <div class="flex items-center gap-4">
        <div class="text-sm uppercase tracking-[0.35em] text-cyan-300/70">
          <span v-if="currentPanel === 'portal'">{{ t('nav.portal') }}</span>
          <span v-else-if="currentPanel === 'table'">{{ t('nav.table') }}</span>
          <span v-else-if="currentPanel === 'kitchen'">{{ t('nav.kitchen') }}</span>
          <span v-else-if="currentPanel === 'admin'">{{ t('nav.admin') }}</span>
          <span v-else>Cafe</span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <router-link
          to="/"
          :class="[
            'rounded-2xl px-4 py-2 text-sm font-medium transition',
            currentPanel === 'portal'
              ? 'bg-cyan-400/15 text-cyan-100 ring-1 ring-cyan-300/40'
              : 'text-slate-400 hover:text-white'
          ]"
        >
          {{ t('nav.portal') }}
        </router-link>
        <router-link
          v-if="canOpenTable"
          to="/table"
          :class="[
            'rounded-2xl px-4 py-2 text-sm font-medium transition',
            currentPanel === 'table'
              ? 'bg-cyan-400/15 text-cyan-100 ring-1 ring-cyan-300/40'
              : 'text-slate-400 hover:text-white'
          ]"
        >
          {{ t('nav.table') }}
        </router-link>
        <button
          v-else
          type="button"
          disabled
          aria-disabled="true"
          class="cursor-not-allowed rounded-2xl px-4 py-2 text-sm font-medium text-slate-500 opacity-40"
        >
          {{ t('nav.table') }}
        </button>
        <router-link
          to="/kitchen"
          :class="[
            'rounded-2xl px-4 py-2 text-sm font-medium transition',
            currentPanel === 'kitchen'
              ? 'bg-cyan-400/15 text-cyan-100 ring-1 ring-cyan-300/40'
              : 'text-slate-400 hover:text-white'
          ]"
        >
          {{ t('nav.kitchen') }}
        </router-link>
        <router-link
          v-if="canOpenAdmin"
          to="/admin"
          :class="[
            'rounded-2xl px-4 py-2 text-sm font-medium transition',
            currentPanel === 'admin'
              ? 'bg-cyan-400/15 text-cyan-100 ring-1 ring-cyan-300/40'
              : 'text-slate-400 hover:text-white'
          ]"
        >
          {{ t('nav.admin') }}
        </router-link>
        <button
          v-else
          type="button"
          disabled
          aria-disabled="true"
          class="cursor-not-allowed rounded-2xl px-4 py-2 text-sm font-medium text-slate-500 opacity-40"
        >
          {{ t('nav.admin') }}
        </button>
        <router-link
          v-if="canOpenPortalOwner"
          to="/portal-owner"
          class="rounded-2xl px-4 py-2 text-sm font-medium text-cyan-100 ring-1 ring-cyan-300/30 transition hover:bg-cyan-400/15"
        >
          {{ t('nav.portalAdmin') }}
        </router-link>

        <div class="border-l border-cyan-400/10 pl-4 ml-2">
          <select
            :value="language"
            @change="setLanguage(($event.target as HTMLSelectElement).value as any)"
            class="rounded-2xl px-3 py-2 text-sm font-semibold uppercase tracking-[0.15em] bg-slate-800/60 text-slate-300 border border-cyan-400/20 hover:border-cyan-400/40 focus:outline-none focus:border-cyan-400/60 transition cursor-pointer"
          >
            <option value="en">EN</option>
            <option value="ru">RU</option>
            <option value="uk">UK</option>
            <option value="de">DE</option>
            <option value="pl">PL</option>
            <option value="es">ES</option>
          </select>
        </div>
      </div>

      <div class="text-sm text-slate-400">
        {{ t('nav.orders') }}: <span class="text-cyan-100 font-semibold">{{ orderCount }}</span>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useOrdersStore } from '../store/orders';
import { useLanguage } from '../composables/useLanguage';
import { useSessionStore } from '../store/session';

const route = useRoute();
const ordersStore = useOrdersStore();
const { language, setLanguage, t } = useLanguage();
const sessionStore = useSessionStore();
// Two independent, individually-allowed portal owners — exact normalized match only.
const PORTAL_OWNER_EMAILS = ['gelferclub@gmail.com', 'chris@obatics.ie'];

const hideGuestTableNav = computed(() => {
  const path = route.path || '';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  return (
    path.startsWith('/table') ||
    hash.includes('/table') ||
    route.matched.some((record) => record.path.startsWith('/table'))
  );
});

const currentPanel = computed(() => {
  const path = route.path;
  if (path === '/') return 'portal';
  if (path.includes('/table')) return 'table';
  if (path.includes('/kitchen')) return 'kitchen';
  if (path.includes('/admin')) return 'admin';
  return 'cafe';
});

const orderCount = computed(() => ordersStore.orders.length);

const canOpenAdmin = computed(() => {
  const role = sessionStore.desktopUser?.role;
  return role !== 'kitchen' && role !== 'staff';
});

const canOpenPortalOwner = computed(() => (
  PORTAL_OWNER_EMAILS.includes(String(sessionStore.desktopUser?.email || '').trim().toLowerCase())
));

const canOpenTable = computed(() => currentPanel.value !== 'kitchen');
</script>
